import { Components } from '@ionic/core';
import { IonCard, IonCardContent, IonCardTitle } from '@ionic/react';
import { ReactElement, ReactNode, useMemo } from 'react';
import { Scratchpad } from '../../../api/graphql/generated';
import i18n from '../../../i18n';
import { DateField } from '../../ui/date-field';
import './scratchpad.css';

export interface ScratchpadCardBaseProps {
  scratchpad: Scratchpad;
  className?: string;
  title?: string;
  leftSubtitle?: string | ReactElement | null;
  rightSubtitle?: string | ReactElement | null;
  children?: ReactNode;
  footer?: ReactNode;
  onClick?: (scratchpad: Partial<Scratchpad>) => void;
  cardProps?: Partial<Components.IonCard>;
}

const ScratchpadCardBase = (props: ScratchpadCardBaseProps): ReactElement => {
  const {
    scratchpad,
    className,
    children,
    footer,
    title,
    leftSubtitle,
    rightSubtitle,
    onClick,
    cardProps,
  } = props;

  const leftSubtitleInternal = useMemo(() => {
    let component = leftSubtitle;

    if (!component) {
      component = (
        <>
          {`${i18n.t('common:scratchpad_last_run')}: `}
          <DateField
            date={scratchpad?.executedAt}
            fallback={i18n.t('common:last_activity_never')}
          />
        </>
      );
    }

    return component;
  }, [leftSubtitle, scratchpad?.executedAt]);

  const rightSubtitleInternal = useMemo(() => {
    let component = rightSubtitle;

    if (!component && scratchpad?.active !== true) {
      component = <>{i18n.t('common:inactive')}</>;
    }

    return component;
  }, [rightSubtitle, scratchpad?.active]);

  return (
    <IonCard
      href={onClick ? '#' : undefined}
      className={['scratchpad-card', className ?? ''].join(' ')}
      onClick={
        onClick
          ? (e) => {
              e.preventDefault();
              if (onClick) {
                onClick(scratchpad);
              }
            }
          : undefined
      }
      {...cardProps}
    >
      <div className="scratchpad-card-header">
        <div className="top-left">{leftSubtitleInternal}</div>
        <div className="top-right">{rightSubtitleInternal}</div>
        <IonCardTitle className="name">
          {title ?? scratchpad?.name}
        </IonCardTitle>
        <p className="description">{scratchpad?.description}</p>
      </div>
      {children && (
        <IonCardContent className="scratchpad-card-content">
          {children}
        </IonCardContent>
      )}
      {footer && <div className="scratchpad-card-footer">{footer}</div>}
    </IonCard>
  );
};

export { ScratchpadCardBase };
